import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  outputToObservable,
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  RdsBadgeComponent,
  RdsButtonComponent,
  RdsDialogService,
  RdsIconComponent,
  RdsLinkComponent,
  RdsProgressSpinnerComponent,
  RdsTabComponent,
  RdsTabsComponent,
} from '@rds/angular-components';
import { isAfter } from 'date-fns';
import { combineLatest, filter, switchMap, tap } from 'rxjs';

import {
  BookingBasicData,
  BookingRequestBaseModel,
  BookingRequestUpdateModel,
  BookingStatusEnum,
  BookingSummaryComponent,
  ChatMessageTypeEnum,
  ConfirmationComponent,
  MessageService,
  MessagesComponent,
  NotificationSourceTypeEnum,
  RoomBookingsCalendarComponent,
} from '@bookly/shared';

import { RoomDetailsCardComponent } from '../../../rooms/components/room-details-card/room-details-card.component';
import { RoomsService } from '../../../rooms/services/rooms.service';
import { RoomBookingFormComponent } from '../../components/room-booking-form/room-booking-form.component';
import { BookingRequestService } from '../../services/booking-request.service';
import { BookingService } from '../../services/booking.service';

@Component({
  selector: 'bus-booking-details-view',
  imports: [
    BookingSummaryComponent,
    RdsLinkComponent,
    RouterLink,
    RdsIconComponent,
    RdsBadgeComponent,
    RoomBookingsCalendarComponent,
    RoomDetailsCardComponent,
    RoomBookingFormComponent,
    MessagesComponent,
    RdsButtonComponent,
    RdsTabsComponent,
    RdsTabComponent,
    RdsProgressSpinnerComponent,
  ],
  templateUrl: './booking-details-view.component.html',
  styleUrl: './booking-details-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingDetailsViewComponent implements OnInit {
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #bookingService = inject(BookingService);
  readonly #bookingRequestService = inject(BookingRequestService);
  readonly #roomsService = inject(RoomsService);
  readonly #messageService = inject(MessageService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialogService = inject(RdsDialogService);

  protected readonly bookingDetails = this.#bookingService.bookingDetails;
  protected readonly roomDetails = this.#roomsService.roomDetails;
  protected readonly roomLayouts = this.#roomsService.roomLayouts;
  protected readonly chatMessages = this.#messageService.chatMessages;
  protected readonly isMessageLoading = this.#messageService.isMessageLoading;
  protected readonly isLoading = computed(
    () =>
      this.#bookingService.isBookingDetailsLoading() ||
      this.#roomsService.isRoomDetailsLoading() ||
      this.#bookingRequestService.isLoading()
  );
  protected readonly NotificationSourceTypeEnum = NotificationSourceTypeEnum;
  protected isPastBooking: boolean = false;
  protected isRequestEditable: boolean = false;
  protected isDeleted: boolean = false;
  protected isEdit = signal(false);
  protected bookingBaseData!: BookingBasicData;
  protected eventDateTime: Date | null = null;
  protected tabs!: RdsTabsComponent;

  @ViewChild('messagesTab', { static: false })
  protected messagesTab!: RdsTabComponent;

  @ViewChild('tabs', { static: false })
  set tabsWrapper(component: RdsTabsComponent) {
    this.tabs = component;
    if (this.tabs) {
      outputToObservable(this.tabs.selectedTabChange)
        .pipe(
          filter(() => this.messagesTab.active()),
          filter(change => change.tab === this.messagesTab),
          filter(() =>
            this.chatMessages().some(message => !message.readByViewer)
          ),
          switchMap(() =>
            this.#messageService.markAllMessagesAsRead(
              this.bookingBaseData.bookingId
            )
          ),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe();
    }
  }

  ngOnInit(): void {
    this.#activatedRoute.params
      .pipe(
        switchMap(({ bookingId, roomId }) => {
          this.bookingBaseData = {
            bookingId: bookingId,
            roomDexId: roomId,
            ...this.#activatedRoute.snapshot.queryParams,
          };
          return combineLatest([
            this.#bookingService.getBookingDetails(bookingId).pipe(
              tap(booking => {
                this.isPastBooking = !isAfter(booking.startTime, new Date());
                this.isRequestEditable =
                  booking.status.value === BookingStatusEnum.InProgress ||
                  booking.status.value === BookingStatusEnum.Approved;
                this.isDeleted =
                  booking.status.value === BookingStatusEnum.Deleted;
              })
            ),
            this.#messageService.getMessages(
              bookingId,
              ChatMessageTypeEnum.Chat
            ),
            this.#roomsService.getRoomData(roomId),
          ]);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  protected deleteBooking() {
    const booking = this.bookingDetails();
    if (booking) {
      const dialog = this.#dialogService.open(ConfirmationComponent, {
        data: {
          header: 'Are you sure you want to delete this booking?',
          message: 'Operation is irreversible',
        },
      });
      dialog
        .afterClosed()
        .pipe(
          takeUntilDestroyed(this.#destroyRef),
          filter(Boolean),
          switchMap(() => {
            return this.#bookingService.deleteBooking(booking.id);
          })
        )
        .subscribe(() => {
          void this.#router.navigate(['dashboard', 'bookings']);
        });
    }
  }

  protected updateRoomBooking(requestData: BookingRequestBaseModel) {
    const request = {
      ...requestData,
      id: this.bookingDetails()?.id,
    } as BookingRequestUpdateModel;
    this.#bookingRequestService
      .updateRoomBooking(request)
      .subscribe(response => {
        this.#bookingService.refreshBookingDetails(response);
        this.cancel();
      });
  }

  protected edit() {
    this.isEdit.set(true);
    this.tabs.selectTab(0);
  }

  protected cancel() {
    this.isEdit.set(false);
    this.eventDateTime = null;
  }

  protected postMessage(message: string) {
    const booking = this.bookingDetails();
    if (booking) {
      this.#messageService
        .postMessage(
          {
            bookingId: booking.id,
            message,
          },
          ChatMessageTypeEnum.Chat
        )
        .subscribe();
    }
  }

  protected fillEventDate(startDate: Date) {
    if (this.isEdit()) {
      this.eventDateTime = startDate;
      this.tabs.selectTab(0);
    }
  }
}
